import React from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Building2, Target, FileSearch, TrendingUp,
  KanbanSquare, Swords, CreditCard, LogOut, HardHat, Settings as SettingsIcon,
  Store, Users, ShieldCheck, Gift, Wallet,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import StartTrial from "@/pages/StartTrial";

// New-era users (post-trial-launch) must start/keep a subscription; legacy free users are grandfathered.
export const needsTrial = (u) =>
  u && !u.is_platform_admin &&
  (u.trial_status === "pending" || u.trial_status === "used") &&
  u.subscription_status !== "active";

const nav = [
  { to: "/dashboard", label: "Overview", icon: LayoutDashboard, end: true, testid: "nav-overview" },
  { to: "/dashboard/profile", label: "Profile Builder", icon: Building2, testid: "nav-profile" },
  { to: "/dashboard/opportunities", label: "Opportunities", icon: Target, testid: "nav-opportunities" },
  { to: "/dashboard/triage", label: "Bid Package AI", icon: FileSearch, testid: "nav-triage" },
  { to: "/dashboard/roadmap", label: "Growth Roadmap", icon: TrendingUp, testid: "nav-roadmap" },
  { to: "/dashboard/crm", label: "Bid CRM", icon: KanbanSquare, testid: "nav-crm" },
  { to: "/dashboard/intel", label: "Competitive Intel", icon: Swords, testid: "nav-intel" },
  { to: "/dashboard/marketplace", label: "Marketplace", icon: Store, testid: "nav-marketplace", adminOnly: true },
  { to: "/dashboard/team", label: "Team", icon: Users, testid: "nav-team" },
  { to: "/dashboard/referrals", label: "Refer & Earn", icon: Gift, testid: "nav-referrals" },
  { to: "/dashboard/billing", label: "Billing & Plans", icon: CreditCard, testid: "nav-billing" },
  { to: "/dashboard/payment-method", label: "Payment Method", icon: Wallet, testid: "nav-payment-method" },
  { to: "/dashboard/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
  { to: "/dashboard/admin", label: "Admin", icon: ShieldCheck, testid: "nav-admin", adminOnly: true },
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const visibleNav = nav.filter((item) => !item.adminOnly || user?.is_platform_admin);

  // Gate the whole dashboard behind the trial screen — unless returning from Stripe (session_id present).
  const hasSession = new URLSearchParams(location.search).has("session_id");
  if (needsTrial(user) && !hasSession) {
    return <StartTrial />;
  }

  return (
    <div className="min-h-screen flex bg-slate-50">
      <aside className="hidden md:flex w-64 flex-col border-r border-slate-200 bg-white sticky top-0 h-screen">
        <div
          className="flex items-center gap-2 px-6 h-16 border-b border-slate-200 cursor-pointer"
          onClick={() => navigate("/dashboard")}
          data-testid="sidebar-logo"
        >
          <div className="h-8 w-8 rounded-lg bg-emerald-900 flex items-center justify-center">
            <HardHat className="h-4 w-4 text-lime-300" strokeWidth={2.2} />
          </div>
          <span className="font-heading font-bold text-lg tracking-tight text-slate-900">BidMatchAI</span>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {visibleNav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={item.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive ? "bg-emerald-900 text-white" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                }`
              }
            >
              <item.icon className="h-[18px] w-[18px]" strokeWidth={2} />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-slate-200 p-3">
          <div className="flex items-center gap-3 px-2 py-2">
            {user?.picture ? (
              <img src={user.picture} alt="" className="h-9 w-9 rounded-full object-cover" />
            ) : (
              <div className="h-9 w-9 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 font-semibold">
                {user?.name?.[0] || "U"}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-900 truncate">{user?.name}</p>
              {user?.is_platform_admin ? (
                <span data-testid="admin-badge" className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide bg-emerald-100 text-emerald-700 rounded-full px-2 py-0.5 mt-0.5">
                  <ShieldCheck className="h-3 w-3" /> Admin · Full access
                </span>
              ) : (
                <p className="text-xs text-slate-400 capitalize">{user?.plan} plan</p>
              )}
            </div>
            <button onClick={logout} data-testid="logout-button" className="text-slate-400 hover:text-slate-900 transition-colors">
              <LogOut className="h-[18px] w-[18px]" />
            </button>
          </div>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile top nav */}
        <div className="md:hidden flex items-center justify-between px-4 h-14 bg-white border-b border-slate-200">
          <span className="font-heading font-bold text-slate-900">BidMatchAI</span>
          <button onClick={logout} className="text-slate-500"><LogOut className="h-5 w-5" /></button>
        </div>
        <div className="md:hidden flex gap-1 overflow-x-auto px-3 py-2 bg-white border-b border-slate-200">
          {visibleNav.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.end}
              className={({ isActive }) => `whitespace-nowrap px-3 py-1.5 rounded-md text-xs font-medium ${isActive ? "bg-emerald-900 text-white" : "text-slate-500 bg-slate-100"}`}>
              {item.label}
            </NavLink>
          ))}
        </div>
        <main className="flex-1 p-5 sm:p-8 max-w-[1400px] w-full mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
